import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import {
  LayoutDashboard,
  ListTodo,
  GitBranch,
  CheckSquare,
  Clock,
  AlertTriangle,
  Plus,
  Play,
  Pause,
  Eye,
  Edit,
  Trash2,
  ChevronRight,
  Calendar,
  Filter,
  Search,
  RefreshCw,
  Sparkles,
  Users,
  Mail,
  FileText,
  Settings,
  ArrowRight,
  Circle,
  Square,
  Diamond,
  Zap,
  CheckCircle,
  XCircle,
  MoreVertical,
  GripVertical,
  Copy,
  Save,
  X,
  Home,
  UserPlus,
  Gavel,
  Palette,
  ArrowLeftRight,
  Snowflake,
  DollarSign,
  FileSignature,
  LayoutTemplate,
  Workflow,
  Timer,
  Vote,
  CircleDot,
  CircleStop,
  Split,
} from "lucide-react";
import AdminSidebar from "../components/layout/AdminSidebar";
import { useAuth } from "../App";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";

// ============ CUSTOM NODE COMPONENTS ============

const TriggerNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[180px] bg-gradient-to-br from-green-50 to-green-100 ${selected ? 'border-green-500 ring-2 ring-green-200' : 'border-green-400'}`}>
    <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-green-500" />
    <div className="flex items-center gap-2">
      <div className="p-1.5 bg-green-500 rounded-md">
        <Zap className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-xs font-medium text-green-600 uppercase">Trigger</div>
        <div className="font-semibold text-green-900 text-sm">{data.label}</div>
      </div>
    </div>
    {data.trigger_type && (
      <div className="mt-2 text-xs text-green-700 bg-green-200/50 px-2 py-1 rounded">
        {data.trigger_type === 'event' ? `On: ${data.event || 'event'}` : data.trigger_type === 'time' ? `Schedule: ${data.schedule || 'scheduled'}` : 'Manual'}
      </div>
    )}
  </div>
);

const ActionNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[180px] bg-gradient-to-br from-blue-50 to-blue-100 ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-blue-400'}`}>
    <Handle type="target" position={Position.Top} className="w-3 h-3 bg-blue-500" />
    <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-blue-500" />
    <div className="flex items-center gap-2">
      <div className="p-1.5 bg-blue-500 rounded-md">
        <Play className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-xs font-medium text-blue-600 uppercase">Action</div>
        <div className="font-semibold text-blue-900 text-sm">{data.label}</div>
      </div>
    </div>
    {data.action && (
      <div className="mt-2 text-xs text-blue-700 bg-blue-200/50 px-2 py-1 rounded">
        {data.action === 'send_email' ? `Email: ${data.template || 'template'}` : data.action === 'create_task' ? `Task: ${data.title || 'task'}` : data.action}
      </div>
    )}
  </div>
);

const ConditionNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[180px] bg-gradient-to-br from-amber-50 to-amber-100 ${selected ? 'border-amber-500 ring-2 ring-amber-200' : 'border-amber-400'}`}>
    <Handle type="target" position={Position.Top} className="w-3 h-3 bg-amber-500" />
    <Handle type="source" position={Position.Bottom} id="true" className="w-3 h-3 bg-green-500" style={{ left: '30%' }} />
    <Handle type="source" position={Position.Bottom} id="false" className="w-3 h-3 bg-red-500" style={{ left: '70%' }} />
    <div className="flex items-center gap-2">
      <div className="p-1.5 bg-amber-500 rounded-md">
        <Split className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-xs font-medium text-amber-600 uppercase">Condition</div>
        <div className="font-semibold text-amber-900 text-sm">{data.label}</div>
      </div>
    </div>
    {data.field && (
      <div className="mt-2 text-xs text-amber-700 bg-amber-200/50 px-2 py-1 rounded">
        {data.field} {data.operator} {data.value}
      </div>
    )}
    <div className="flex justify-between mt-2 text-xs">
      <span className="text-green-600">✓ Yes</span>
      <span className="text-red-600">✗ No</span>
    </div>
  </div>
);

const WaitNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[180px] bg-gradient-to-br from-purple-50 to-purple-100 ${selected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-purple-400'}`}>
    <Handle type="target" position={Position.Top} className="w-3 h-3 bg-purple-500" />
    <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-purple-500" />
    <div className="flex items-center gap-2">
      <div className="p-1.5 bg-purple-500 rounded-md">
        <Timer className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-xs font-medium text-purple-600 uppercase">Wait</div>
        <div className="font-semibold text-purple-900 text-sm">{data.label}</div>
      </div>
    </div>
    {data.duration && (
      <div className="mt-2 text-xs text-purple-700 bg-purple-200/50 px-2 py-1 rounded">
        Duration: {data.duration}
      </div>
    )}
  </div>
);

const ApprovalNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[180px] bg-gradient-to-br from-orange-50 to-orange-100 ${selected ? 'border-orange-500 ring-2 ring-orange-200' : 'border-orange-400'}`}>
    <Handle type="target" position={Position.Top} className="w-3 h-3 bg-orange-500" />
    <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-orange-500" />
    <div className="flex items-center gap-2">
      <div className="p-1.5 bg-orange-500 rounded-md">
        <Vote className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-xs font-medium text-orange-600 uppercase">Approval</div>
        <div className="font-semibold text-orange-900 text-sm">{data.label}</div>
      </div>
    </div>
    {data.approvers && (
      <div className="mt-2 text-xs text-orange-700 bg-orange-200/50 px-2 py-1 rounded">
        Approvers: {Array.isArray(data.approvers) ? data.approvers.join(', ') : data.approvers}
      </div>
    )}
  </div>
);

const EndNode = ({ data, selected }) => (
  <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[180px] bg-gradient-to-br from-gray-50 to-gray-100 ${selected ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-400'}`}>
    <Handle type="target" position={Position.Top} className="w-3 h-3 bg-gray-500" />
    <div className="flex items-center gap-2">
      <div className="p-1.5 bg-gray-500 rounded-md">
        <CircleStop className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-xs font-medium text-gray-600 uppercase">End</div>
        <div className="font-semibold text-gray-900 text-sm">{data.label}</div>
      </div>
    </div>
  </div>
);

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  wait: WaitNode,
  approval: ApprovalNode,
  end: EndNode,
};

// Default edge options
const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
  },
  style: {
    strokeWidth: 2,
    stroke: '#64748b',
  },
};

// ============ TEMPLATE ICONS ============
const getTemplateIcon = (templateId) => {
  const icons = {
    'TPL-WF-ONBOARD': <UserPlus className="w-5 h-5" />,
    'TPL-WF-MEETING': <Calendar className="w-5 h-5" />,
    'TPL-WF-HEARING': <Gavel className="w-5 h-5" />,
    'TPL-WF-DRC': <Palette className="w-5 h-5" />,
    'TPL-WF-TRANSFER': <ArrowLeftRight className="w-5 h-5" />,
    'TPL-WF-SEASONAL': <Snowflake className="w-5 h-5" />,
    'TPL-WF-BUDGET': <DollarSign className="w-5 h-5" />,
    'TPL-WF-VENDOR': <FileSignature className="w-5 h-5" />,
  };
  return icons[templateId] || <Workflow className="w-5 h-5" />;
};

// ============ MAIN COMPONENT ============

const WorkflowHubPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [reminders, setReminders] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Task form state
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    module: "general",
    priority: "normal",
    due_date: "",
    assigned_to: ""
  });
  
  // Workflow builder state
  const [showWorkflowBuilder, setShowWorkflowBuilder] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [workflowName, setWorkflowName] = useState("");
  const [workflowDescription, setWorkflowDescription] = useState("");
  const [workflowModule, setWorkflowModule] = useState("general");
  const [workflowActive, setWorkflowActive] = useState(true);
  const [workflowTrigger, setWorkflowTrigger] = useState({ type: "manual", config: {} });
  
  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Node editor dialog
  const [showNodeEditor, setShowNodeEditor] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  
  // Template picker
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, tasksRes, workflowsRes, approvalsRes, remindersRes, templatesRes, executionsRes] = await Promise.all([
        axios.get(`${API}/workflow/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/workflow/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/workflow/workflows`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/workflow/approvals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/workflow/reminders?upcoming_days=7`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/workflow/templates`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { templates: [] } })),
        axios.get(`${API}/workflow/executions?limit=20`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { executions: [] } }))
      ]);
      
      setDashboard(dashboardRes.data);
      setTasks(tasksRes.data.tasks || []);
      setWorkflows(workflowsRes.data.workflows || []);
      setApprovals(approvalsRes.data.approvals || []);
      setReminders(remindersRes.data);
      setTemplates(templatesRes.data.templates || []);
      setExecutions(executionsRes.data.executions || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load workflow hub data");
    } finally {
      setLoading(false);
    }
  };

  // ============ TASK HANDLERS ============
  
  const handleCreateTask = async () => {
    if (!newTask.title) {
      toast.error("Please enter a task title");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/workflow/tasks`,
        newTask,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Task ${response.data.task_id} created`);
      setShowTaskForm(false);
      setNewTask({
        title: "",
        description: "",
        module: "general",
        priority: "normal",
        due_date: "",
        assigned_to: ""
      });
      fetchData();
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(
        `${API}/workflow/tasks/${taskId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Task completed");
      fetchData();
    } catch (error) {
      console.error("Error completing task:", error);
      toast.error("Failed to complete task");
    }
  };

  const handleUpdateTaskStatus = async (taskId, status) => {
    try {
      await axios.put(
        `${API}/workflow/tasks/${taskId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Task updated");
      fetchData();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm("Delete this task?")) return;
    try {
      await axios.delete(
        `${API}/workflow/tasks/${taskId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Task deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  // ============ WORKFLOW HANDLERS ============

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions }, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const openWorkflowBuilder = (workflow = null) => {
    if (workflow) {
      setEditingWorkflow(workflow);
      setWorkflowName(workflow.name);
      setWorkflowDescription(workflow.description || "");
      setWorkflowModule(workflow.module || "general");
      setWorkflowActive(workflow.is_active);
      setWorkflowTrigger(workflow.trigger || { type: "manual", config: {} });
      setNodes(workflow.nodes || []);
      setEdges(workflow.edges || []);
    } else {
      setEditingWorkflow(null);
      setWorkflowName("");
      setWorkflowDescription("");
      setWorkflowModule("general");
      setWorkflowActive(true);
      setWorkflowTrigger({ type: "manual", config: {} });
      setNodes([]);
      setEdges([]);
    }
    setSelectedNode(null);
    setShowWorkflowBuilder(true);
  };

  const closeWorkflowBuilder = () => {
    setShowWorkflowBuilder(false);
    setEditingWorkflow(null);
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
  };

  const addNode = (type) => {
    const nodeId = `${type}-${Date.now()}`;
    const newNode = {
      id: nodeId,
      type,
      position: { x: 250, y: (nodes.length * 120) + 50 },
      data: getDefaultNodeData(type)
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedNode(newNode);
  };

  const getDefaultNodeData = (type) => {
    switch (type) {
      case "trigger":
        return { label: "Start Trigger", trigger_type: "manual" };
      case "action":
        return { label: "New Action", action: "send_email", template: "", title: "" };
      case "condition":
        return { label: "Check Condition", field: "", operator: "equals", value: "" };
      case "approval":
        return { label: "Request Approval", approvers: [], timeout_days: 7 };
      case "wait":
        return { label: "Wait Period", duration: "1 day" };
      case "end":
        return { label: "End" };
      default:
        return { label: type };
    }
  };

  const updateSelectedNode = (updates) => {
    if (!selectedNode) return;
    
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, ...updates } }
          : node
      )
    );
    setSelectedNode((prev) => prev ? { ...prev, data: { ...prev.data, ...updates } } : null);
  };

  const deleteSelectedNode = () => {
    if (!selectedNode) return;
    
    setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setSelectedNode(null);
  };

  const handleSaveWorkflow = async () => {
    if (!workflowName) {
      toast.error("Please enter a workflow name");
      return;
    }
    
    const workflowData = {
      name: workflowName,
      description: workflowDescription,
      module: workflowModule,
      trigger: workflowTrigger,
      nodes: nodes,
      edges: edges,
      is_active: workflowActive
    };
    
    try {
      if (editingWorkflow) {
        await axios.put(
          `${API}/workflow/workflows/${editingWorkflow.workflow_id}`,
          workflowData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Workflow updated");
      } else {
        await axios.post(
          `${API}/workflow/workflows`,
          workflowData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Workflow created");
      }
      closeWorkflowBuilder();
      fetchData();
    } catch (error) {
      console.error("Error saving workflow:", error);
      toast.error(error.response?.data?.detail || "Failed to save workflow");
    }
  };

  const handleExecuteWorkflow = async (workflowId) => {
    try {
      const response = await axios.post(
        `${API}/workflow/workflows/${workflowId}/execute`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Workflow started: ${response.data.execution_id}`);
      fetchData();
    } catch (error) {
      console.error("Error executing workflow:", error);
      toast.error("Failed to execute workflow");
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!confirm("Delete this workflow?")) return;
    try {
      await axios.delete(
        `${API}/workflow/workflows/${workflowId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Workflow deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting workflow:", error);
      toast.error("Failed to delete workflow");
    }
  };

  const handleCreateFromTemplate = async (templateId) => {
    try {
      const response = await axios.post(
        `${API}/workflow/templates/${templateId}/create-workflow`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Workflow created from template!");
      setShowTemplatePicker(false);
      fetchData();
      
      // Open the new workflow for editing
      const newWorkflow = await axios.get(
        `${API}/workflow/workflows/${response.data.workflow_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      openWorkflowBuilder(newWorkflow.data);
    } catch (error) {
      console.error("Error creating from template:", error);
      toast.error(error.response?.data?.detail || "Failed to create workflow");
    }
  };

  const handleLoadTemplate = (template) => {
    setWorkflowName(template.name);
    setWorkflowDescription(template.description);
    setWorkflowModule(template.module);
    setWorkflowTrigger(template.trigger);
    setNodes(template.nodes);
    setEdges(template.edges);
    setShowTemplatePicker(false);
  };

  // ============ APPROVAL HANDLERS ============

  const handleVoteApproval = async (approvalId, decision) => {
    try {
      await axios.post(
        `${API}/workflow/approvals/${approvalId}/vote`,
        { decision, comments: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Vote recorded: ${decision}`);
      fetchData();
    } catch (error) {
      console.error("Error voting:", error);
      toast.error(error.response?.data?.detail || "Failed to vote");
    }
  };

  // ============ HELPERS ============

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: "bg-yellow-100 text-yellow-800", label: "Pending" },
      in_progress: { color: "bg-blue-100 text-blue-800", label: "In Progress" },
      completed: { color: "bg-green-100 text-green-800", label: "Completed" },
      cancelled: { color: "bg-gray-100 text-gray-800", label: "Cancelled" },
      waiting: { color: "bg-purple-100 text-purple-800", label: "Waiting" },
      approved: { color: "bg-green-100 text-green-800", label: "Approved" },
      denied: { color: "bg-red-100 text-red-800", label: "Denied" },
      running: { color: "bg-blue-100 text-blue-800", label: "Running" },
      failed: { color: "bg-red-100 text-red-800", label: "Failed" }
    };
    const c = config[status] || { color: "bg-gray-100", label: status };
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { color: "bg-gray-100 text-gray-600" },
      normal: { color: "bg-blue-100 text-blue-600" },
      high: { color: "bg-orange-100 text-orange-600" },
      urgent: { color: "bg-red-100 text-red-600" }
    };
    const c = config[priority] || config.normal;
    return <Badge className={c.color}>{priority}</Badge>;
  };

  const { user } = useAuth();
  const isBoard = ["admin", "board", "property_manager", "drc"].includes(user?.role);

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <LayoutDashboard className="w-12 h-12 text-primary mx-auto animate-pulse" />
            <p className="mt-4 text-muted-foreground">Loading Workflow Hub...</p>
          </div>
        </div>
      </AdminSidebar>
    );
  }

  // ============ WORKFLOW BUILDER VIEW ============

  if (showWorkflowBuilder) {
    return (
      <div className="h-screen bg-background flex flex-col" data-testid="workflow-builder">
        {/* Builder Header */}
        <header className="bg-card border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={closeWorkflowBuilder}>
              <X className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-serif text-lg font-semibold flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-purple-500" />
                {editingWorkflow ? 'Edit Workflow' : 'New Workflow'}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="workflow-active" className="text-sm">Active</Label>
              <Switch 
                id="workflow-active"
                checked={workflowActive}
                onCheckedChange={setWorkflowActive}
              />
            </div>
            <Button onClick={handleSaveWorkflow} data-testid="save-workflow-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Workflow
            </Button>
          </div>
        </header>

        <div className="flex-1 flex">
          {/* Left Sidebar - Node Palette */}
          <div className="w-64 bg-card border-r p-4">
            <div className="space-y-4">
              <div>
                <Label>Workflow Name *</Label>
                <Input 
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  placeholder="e.g., New Owner Onboarding"
                  data-testid="workflow-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  placeholder="What does this workflow do?"
                  rows={2}
                />
              </div>
              <div>
                <Label>Module</Label>
                <Select value={workflowModule} onValueChange={setWorkflowModule}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="owners">Owners</SelectItem>
                    <SelectItem value="violations">Notices</SelectItem>
                    <SelectItem value="drc">DRC</SelectItem>
                    <SelectItem value="meetings">Meetings</SelectItem>
                    <SelectItem value="payments">Payments</SelectItem>
                    <SelectItem value="vendors">Vendors</SelectItem>
                    <SelectItem value="budgets">Budgets</SelectItem>
                    <SelectItem value="listing">Listing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Add Nodes</Label>
                <p className="text-xs text-muted-foreground mb-3">Drag or click to add</p>
                <div className="space-y-2">
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNode('trigger')}>
                    <Zap className="w-4 h-4 mr-2 text-green-500" /> Trigger
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNode('action')}>
                    <Play className="w-4 h-4 mr-2 text-blue-500" /> Action
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNode('condition')}>
                    <Split className="w-4 h-4 mr-2 text-amber-500" /> Condition
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNode('wait')}>
                    <Timer className="w-4 h-4 mr-2 text-purple-500" /> Wait
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNode('approval')}>
                    <Vote className="w-4 h-4 mr-2 text-orange-500" /> Approval
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNode('end')}>
                    <CircleStop className="w-4 h-4 mr-2 text-gray-500" /> End
                  </Button>
                </div>
              </div>

              <Separator />

              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => setShowTemplatePicker(true)}
              >
                <LayoutTemplate className="w-4 h-4 mr-2" />
                Load Template
              </Button>
            </div>
          </div>

          {/* Main Canvas */}
          <div className="flex-1 relative">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              nodeTypes={nodeTypes}
              defaultEdgeOptions={defaultEdgeOptions}
              fitView
              snapToGrid
              snapGrid={[15, 15]}
              className="bg-slate-50"
            >
              <Controls />
              <Background variant="dots" gap={20} size={1} />
              <Panel position="top-center">
                <div className="bg-white/90 backdrop-blur px-4 py-2 rounded-lg shadow-sm border text-sm text-muted-foreground">
                  Click nodes to edit • Drag to reposition • Connect handles to link
                </div>
              </Panel>
            </ReactFlow>
          </div>

          {/* Right Sidebar - Node Editor */}
          <div className="w-72 bg-card border-l p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Node Properties
            </h3>
            
            {selectedNode ? (
              <div className="space-y-4">
                <div>
                  <Label>Label</Label>
                  <Input 
                    value={selectedNode.data.label || ''}
                    onChange={(e) => updateSelectedNode({ label: e.target.value })}
                  />
                </div>

                {selectedNode.type === 'trigger' && (
                  <>
                    <div>
                      <Label>Trigger Type</Label>
                      <Select 
                        value={selectedNode.data.trigger_type || 'manual'}
                        onValueChange={(v) => updateSelectedNode({ trigger_type: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manual</SelectItem>
                          <SelectItem value="event">On Event</SelectItem>
                          <SelectItem value="time">Scheduled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedNode.data.trigger_type === 'event' && (
                      <div>
                        <Label>Event</Label>
                        <Select 
                          value={selectedNode.data.event || ''}
                          onValueChange={(v) => updateSelectedNode({ event: v })}
                        >
                          <SelectTrigger><SelectValue placeholder="Select event" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner_created">Owner Created</SelectItem>
                            <SelectItem value="violation_created">Violation Created</SelectItem>
                            <SelectItem value="drc_submission_created">DRC Submission</SelectItem>
                            <SelectItem value="payment_received">Payment Received</SelectItem>
                            <SelectItem value="property_transfer_initiated">Property Transfer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}

                {selectedNode.type === 'action' && (
                  <>
                    <div>
                      <Label>Action Type</Label>
                      <Select 
                        value={selectedNode.data.action || 'send_email'}
                        onValueChange={(v) => updateSelectedNode({ action: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="send_email">Send Email</SelectItem>
                          <SelectItem value="create_task">Create Task</SelectItem>
                          <SelectItem value="update_status">Update Status</SelectItem>
                          <SelectItem value="create_approval">Create Approval</SelectItem>
                          <SelectItem value="generate_disclosure">Generate Disclosure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedNode.data.action === 'send_email' && (
                      <>
                        <div>
                          <Label>Email Template</Label>
                          <Input 
                            value={selectedNode.data.template || ''}
                            onChange={(e) => updateSelectedNode({ template: e.target.value })}
                            placeholder="template_name"
                          />
                        </div>
                        <div>
                          <Label>Subject</Label>
                          <Input 
                            value={selectedNode.data.subject || ''}
                            onChange={(e) => updateSelectedNode({ subject: e.target.value })}
                            placeholder="Email subject"
                          />
                        </div>
                      </>
                    )}
                    {selectedNode.data.action === 'create_task' && (
                      <>
                        <div>
                          <Label>Task Title</Label>
                          <Input 
                            value={selectedNode.data.title || ''}
                            onChange={(e) => updateSelectedNode({ title: e.target.value })}
                            placeholder="Task title"
                          />
                        </div>
                        <div>
                          <Label>Assign To</Label>
                          <Select 
                            value={selectedNode.data.assign_to || ''}
                            onValueChange={(v) => updateSelectedNode({ assign_to: v })}
                          >
                            <SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="property_manager">Property Manager</SelectItem>
                              <SelectItem value="board">Board</SelectItem>
                              <SelectItem value="drc">DRC</SelectItem>
                              <SelectItem value="accountant">Accountant</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Priority</Label>
                          <Select 
                            value={selectedNode.data.priority || 'normal'}
                            onValueChange={(v) => updateSelectedNode({ priority: v })}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </>
                )}

                {selectedNode.type === 'condition' && (
                  <>
                    <div>
                      <Label>Field to Check</Label>
                      <Input 
                        value={selectedNode.data.field || ''}
                        onChange={(e) => updateSelectedNode({ field: e.target.value })}
                        placeholder="e.g., status"
                      />
                    </div>
                    <div>
                      <Label>Operator</Label>
                      <Select 
                        value={selectedNode.data.operator || 'equals'}
                        onValueChange={(v) => updateSelectedNode({ operator: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">Equals</SelectItem>
                          <SelectItem value="not_equals">Not Equals</SelectItem>
                          <SelectItem value="contains">Contains</SelectItem>
                          <SelectItem value="greater_than">Greater Than</SelectItem>
                          <SelectItem value="less_than">Less Than</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Value</Label>
                      <Input 
                        value={selectedNode.data.value || ''}
                        onChange={(e) => updateSelectedNode({ value: e.target.value })}
                        placeholder="Expected value"
                      />
                    </div>
                  </>
                )}

                {selectedNode.type === 'wait' && (
                  <div>
                    <Label>Duration</Label>
                    <Select 
                      value={selectedNode.data.duration || '1 day'}
                      onValueChange={(v) => updateSelectedNode({ duration: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1 hour">1 Hour</SelectItem>
                        <SelectItem value="1 day">1 Day</SelectItem>
                        <SelectItem value="2 days">2 Days</SelectItem>
                        <SelectItem value="3 days">3 Days</SelectItem>
                        <SelectItem value="7 days">7 Days</SelectItem>
                        <SelectItem value="14 days">14 Days</SelectItem>
                        <SelectItem value="30 days">30 Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {selectedNode.type === 'approval' && (
                  <>
                    <div>
                      <Label>Approvers</Label>
                      <Select 
                        value={selectedNode.data.approvers?.[0] || ''}
                        onValueChange={(v) => updateSelectedNode({ approvers: [v] })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select approvers" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="board">Board</SelectItem>
                          <SelectItem value="drc">DRC Committee</SelectItem>
                          <SelectItem value="property_manager">Property Manager</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Timeout (days)</Label>
                      <Input 
                        type="number"
                        value={selectedNode.data.timeout_days || 7}
                        onChange={(e) => updateSelectedNode({ timeout_days: parseInt(e.target.value) })}
                      />
                    </div>
                  </>
                )}

                <Separator />

                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="w-full"
                  onClick={deleteSelectedNode}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Node
                </Button>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Select a node to edit its properties</p>
              </div>
            )}
          </div>
        </div>

        {/* Template Picker Dialog */}
        <Dialog open={showTemplatePicker} onOpenChange={setShowTemplatePicker}>
          <DialogContent className="max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Load Workflow Template</DialogTitle>
              <DialogDescription>Choose a pre-built template to customize</DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[500px] pr-4">
              <div className="grid grid-cols-2 gap-4">
                {templates.map((template) => (
                  <Card 
                    key={template.template_id}
                    className="cursor-pointer hover:border-primary transition-colors"
                    onClick={() => handleLoadTemplate(template)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                          {getTemplateIcon(template.template_id)}
                        </div>
                        <div>
                          <CardTitle className="text-base">{template.name}</CardTitle>
                          <Badge variant="outline" className="mt-1">{template.category}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2">{template.description}</p>
                      <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                        <span>{template.nodes?.length || 0} nodes</span>
                        <span>•</span>
                        <span>{template.module}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ MAIN DASHBOARD VIEW ============

  return (
    <AdminSidebar>
    <div className="min-h-screen bg-background" data-testid="workflow-hub-page">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/board")}>
              <ChevronRight className="w-5 h-5 rotate-180" />
            </Button>
            <div>
              <h1 className="font-serif text-xl font-semibold flex items-center gap-2">
                <LayoutDashboard className="w-5 h-5 text-purple-500" />
                Workflow Hub
              </h1>
              <p className="text-xs text-muted-foreground">Tasks, Workflows & Approvals</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Dialog open={showTaskForm} onOpenChange={setShowTaskForm}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="new-task-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Task
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Task</DialogTitle>
                  <DialogDescription>Add a new task to your workflow</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Title *</Label>
                    <Input 
                      value={newTask.title}
                      onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                      placeholder="Task title..."
                      data-testid="task-title-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea 
                      value={newTask.description}
                      onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                      placeholder="Task details..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Module</Label>
                      <Select value={newTask.module} onValueChange={(v) => setNewTask({...newTask, module: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="violations">Notices</SelectItem>
                          <SelectItem value="drc">DRC</SelectItem>
                          <SelectItem value="payments">Payments</SelectItem>
                          <SelectItem value="meetings">Meetings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select value={newTask.priority} onValueChange={(v) => setNewTask({...newTask, priority: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Due Date</Label>
                      <Input 
                        type="date"
                        value={newTask.due_date}
                        onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Assign To</Label>
                      <Input 
                        value={newTask.assigned_to}
                        onChange={(e) => setNewTask({...newTask, assigned_to: e.target.value})}
                        placeholder="email or role"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={() => setShowTaskForm(false)}>Cancel</Button>
                  <Button onClick={handleCreateTask} data-testid="create-task-btn">Create Task</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Button onClick={() => openWorkflowBuilder()} data-testid="new-workflow-btn">
              <GitBranch className="w-4 h-4 mr-2" />
              Build Workflow
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="gap-2">
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2">
              <ListTodo className="w-4 h-4" /> Tasks
            </TabsTrigger>
            <TabsTrigger value="workflows" className="gap-2">
              <GitBranch className="w-4 h-4" /> Workflows
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-2">
              <LayoutTemplate className="w-4 h-4" /> Templates
            </TabsTrigger>
            <TabsTrigger value="approvals" className="gap-2">
              <CheckSquare className="w-4 h-4" /> Approvals
            </TabsTrigger>
            <TabsTrigger value="executions" className="gap-2">
              <RefreshCw className="w-4 h-4" /> History
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Tasks Due Today</p>
                      <p className="text-3xl font-bold text-orange-600">{dashboard?.summary?.tasks_due_today || 0}</p>
                    </div>
                    <Calendar className="w-8 h-8 text-orange-500 opacity-70" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Overdue Tasks</p>
                      <p className="text-3xl font-bold text-red-600">{dashboard?.summary?.overdue_tasks || 0}</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-red-500 opacity-70" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Approvals</p>
                      <p className="text-3xl font-bold text-purple-600">{dashboard?.summary?.pending_approvals || 0}</p>
                    </div>
                    <CheckSquare className="w-8 h-8 text-purple-500 opacity-70" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active Workflows</p>
                      <p className="text-3xl font-bold text-green-600">{dashboard?.summary?.active_workflows || 0}</p>
                    </div>
                    <GitBranch className="w-8 h-8 text-green-500 opacity-70" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Items */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ListTodo className="w-5 h-5" /> Recent Tasks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_tasks?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_tasks.map((task) => (
                        <div key={task.task_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{task.title}</p>
                            <p className="text-xs text-muted-foreground">{task.module} • {task.created_at?.split('T')[0]}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getPriorityBadge(task.priority)}
                            {getStatusBadge(task.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No recent tasks</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckSquare className="w-5 h-5" /> Recent Approvals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_approvals?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_approvals.map((approval) => (
                        <div key={approval.approval_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{approval.title}</p>
                            <p className="text-xs text-muted-foreground">{approval.item_type} • {approval.created_at?.split('T')[0]}</p>
                          </div>
                          {getStatusBadge(approval.status)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No recent approvals</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Overdue & Upcoming */}
            {(reminders?.overdue_tasks?.length > 0 || reminders?.upcoming_tasks?.length > 0) && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-500" /> Attention Required
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {reminders?.overdue_tasks?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-red-600 mb-2">Overdue Tasks ({reminders.overdue_tasks.length})</h4>
                        <div className="space-y-2">
                          {reminders.overdue_tasks.slice(0, 5).map((task) => (
                            <div key={task.task_id} className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded">
                              <span className="text-sm">{task.title}</span>
                              <span className="text-xs text-red-600">Due: {task.due_date?.split('T')[0]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {reminders?.upcoming_tasks?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-orange-600 mb-2">Due This Week ({reminders.upcoming_tasks.length})</h4>
                        <div className="space-y-2">
                          {reminders.upcoming_tasks.slice(0, 5).map((task) => (
                            <div key={task.task_id} className="flex items-center justify-between p-2 bg-orange-50 border border-orange-200 rounded">
                              <span className="text-sm">{task.title}</span>
                              <span className="text-xs text-orange-600">Due: {task.due_date?.split('T')[0]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>All Tasks</CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {tasks.length > 0 ? (
                  <div className="space-y-3">
                    {tasks.map((task) => (
                      <div key={task.task_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{task.title}</p>
                            {getPriorityBadge(task.priority)}
                            {getStatusBadge(task.status)}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {task.module} • Created: {task.created_at?.split('T')[0]}
                            {task.due_date && ` • Due: ${task.due_date?.split('T')[0]}`}
                            {task.assigned_to && ` • Assigned: ${task.assigned_to}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {task.status !== 'completed' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleUpdateTaskStatus(task.task_id, 'in_progress')}
                              >
                                Start
                              </Button>
                              <Button 
                                size="sm"
                                onClick={() => handleCompleteTask(task.task_id)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Complete
                              </Button>
                            </>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => handleDeleteTask(task.task_id)}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No tasks found. Create one to get started!</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Workflows Tab */}
          <TabsContent value="workflows">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Your Workflows</CardTitle>
                  <Button onClick={() => openWorkflowBuilder()}>
                    <Plus className="w-4 h-4 mr-2" /> Create Workflow
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {workflows.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {workflows.map((workflow) => (
                      <Card key={workflow.workflow_id} className="border">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-purple-100 rounded-lg">
                                <GitBranch className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <CardTitle className="text-base">{workflow.name}</CardTitle>
                                <CardDescription className="text-xs">{workflow.module}</CardDescription>
                              </div>
                            </div>
                            <Badge variant={workflow.is_active ? "default" : "secondary"}>
                              {workflow.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{workflow.description || 'No description'}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {workflow.nodes?.length || 0} nodes • {workflow.edges?.length || 0} connections
                            </span>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => openWorkflowBuilder(workflow)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              {workflow.is_active && (
                                <Button size="sm" onClick={() => handleExecuteWorkflow(workflow.workflow_id)}>
                                  <Play className="w-4 h-4" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteWorkflow(workflow.workflow_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <GitBranch className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No workflows yet. Create one or use a template!</p>
                    <div className="flex justify-center gap-2">
                      <Button onClick={() => openWorkflowBuilder()}>
                        <Plus className="w-4 h-4 mr-2" /> Create from Scratch
                      </Button>
                      <Button variant="outline" onClick={() => setActiveTab('templates')}>
                        <LayoutTemplate className="w-4 h-4 mr-2" /> Browse Templates
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <div className="mb-4">
              <h2 className="text-xl font-semibold mb-2">Pre-built Workflow Templates</h2>
              <p className="text-muted-foreground">Choose a template to quickly create common HOA workflows</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.template_id} className="hover:border-primary transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl text-primary">
                        {getTemplateIcon(template.template_id)}
                      </div>
                      <div>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline">{template.category}</Badge>
                          <Badge variant="secondary">{template.module}</Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">{template.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {template.nodes?.length || 0} steps
                      </span>
                      <Button size="sm" onClick={() => handleCreateFromTemplate(template.template_id)} data-testid={`use-template-${template.template_id}`}>
                        <Copy className="w-4 h-4 mr-2" /> Use Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals">
            <Card>
              <CardHeader>
                <CardTitle>Pending Approvals</CardTitle>
              </CardHeader>
              <CardContent>
                {approvals.length > 0 ? (
                  <div className="space-y-4">
                    {approvals.map((approval) => (
                      <div key={approval.approval_id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{approval.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">{approval.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline">{approval.item_type}</Badge>
                              {getStatusBadge(approval.status)}
                              <span className="text-xs text-muted-foreground">
                                Requested by: {approval.requested_by}
                              </span>
                            </div>
                            {approval.votes?.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Votes: {approval.votes.length} / {approval.approvers?.length || 0}
                              </p>
                            )}
                          </div>
                          {approval.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleVoteApproval(approval.approval_id, 'approved')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1 text-green-600" /> Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleVoteApproval(approval.approval_id, 'denied')}
                              >
                                <XCircle className="w-4 h-4 mr-1 text-red-600" /> Deny
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No pending approvals</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Executions Tab */}
          <TabsContent value="executions">
            <Card>
              <CardHeader>
                <CardTitle>Workflow Execution History</CardTitle>
              </CardHeader>
              <CardContent>
                {executions.length > 0 ? (
                  <div className="space-y-3">
                    {executions.map((exec) => (
                      <div key={exec.execution_id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{exec.workflow_name || exec.workflow_id}</p>
                            {getStatusBadge(exec.status)}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Started: {new Date(exec.created_at).toLocaleString()}
                            {exec.completed_at && ` • Completed: ${new Date(exec.completed_at).toLocaleString()}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Triggered by: {exec.triggered_by}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">
                            {exec.nodes_completed?.length || 0} nodes completed
                          </p>
                          {exec.error && (
                            <p className="text-xs text-red-600">{exec.error}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No workflow executions yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </AdminSidebar>
  );
};

export default WorkflowHubPage;
